import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  type AddCompetitorDto,
  type CompetitorBenchmarkListDto,
  type CompetitorBoardSummaryDto,
  type CompetitorCompareListDto,
  type CompetitorNearbyListDto,
  type CompetitorNearbyRadius,
  CompetitorsApi,
  ReputationApi,
} from '@up2stay/up2promo-api-client';

import { useLocale } from '@/core/hooks/use-locale';
import { reputationQueryKeys } from '@/core/query-keys/reputation-query-keys';

const STALE_TIME = 60 * 1000;

function invalidateCompetitorQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  void queryClient.invalidateQueries({
    queryKey: reputationQueryKeys.competitorsRoot,
  });
  void queryClient.invalidateQueries({
    queryKey: reputationQueryKeys.overviewRoot,
  });
}

type CompareScope = {
  locationId?: string;
  from?: string;
  to?: string;
};

export function useCompetitorCompare(
  workspaceId: string | undefined,
  scope: CompareScope = {},
) {
  const locale = useLocale();
  return useQuery({
    queryKey: reputationQueryKeys.competitorsCompare(
      workspaceId,
      scope.locationId ?? null,
      scope.from ?? null,
      scope.to ?? null,
      locale,
    ),
    queryFn: async () => {
      const query: Record<string, string> = {};
      if (scope.locationId) query.locationId = scope.locationId;
      if (scope.from) query.from = scope.from;
      if (scope.to) query.to = scope.to;
      const res = await ReputationApi.getCompetitors({
        path: { workspaceId: workspaceId as string },
        query,
      });
      return res.data as CompetitorCompareListDto | undefined;
    },
    enabled: !!workspaceId,
    staleTime: STALE_TIME,
  });
}

export function useAddCompetitor(
  workspaceId: string | undefined,
  locationId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: AddCompetitorDto) => {
      const res = await CompetitorsApi.add({
        path: {
          workspaceId: workspaceId as string,
          locationId: locationId as string,
        },
        body: dto,
      });
      return res.data;
    },
    onSuccess: () => {
      invalidateCompetitorQueries(queryClient);
    },
  });
}

export function useListCompetitors(
  workspaceId: string | undefined,
  locationId: string | undefined,
) {
  const locale = useLocale();
  return useQuery({
    queryKey: reputationQueryKeys.competitorsList(
      workspaceId,
      locationId,
      locale,
    ),
    queryFn: async () => {
      const res = await CompetitorsApi.list({
        path: {
          workspaceId: workspaceId as string,
          locationId: locationId as string,
        },
      });
      return res.data as CompetitorBenchmarkListDto | undefined;
    },
    enabled: !!workspaceId && !!locationId,
    staleTime: STALE_TIME,
  });
}

export function useCompetitorNearby(
  workspaceId: string | undefined,
  locationId: string | undefined,
  radiusMeters?: CompetitorNearbyRadius,
) {
  const locale = useLocale();
  return useQuery({
    queryKey: reputationQueryKeys.competitorsNearby(
      workspaceId,
      locationId,
      locale,
      radiusMeters,
    ),
    queryFn: async () => {
      const res = await CompetitorsApi.nearby({
        path: {
          workspaceId: workspaceId as string,
          locationId: locationId as string,
        },
        query: radiusMeters ? { radiusMeters } : undefined,
      });
      return res.data as CompetitorNearbyListDto | undefined;
    },
    enabled: !!workspaceId && !!locationId,
    staleTime: STALE_TIME,
    // Radius changes swap the query key — keep the map populated meanwhile.
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Cached AI briefing for the competitive board (polls while generating). */
export function useCompetitorBoardSummary(
  workspaceId: string | undefined,
  locationId: string | undefined,
) {
  const locale = useLocale();
  return useQuery({
    queryKey: reputationQueryKeys.competitorsSummary(
      workspaceId,
      locationId,
      locale,
    ),
    queryFn: async () => {
      const res = await CompetitorsApi.summary({
        path: {
          workspaceId: workspaceId as string,
          locationId: locationId as string,
        },
      });
      return res.data as CompetitorBoardSummaryDto | undefined;
    },
    enabled: !!workspaceId && !!locationId,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.status === 'generating' ? 2500 : false,
  });
}

export function useRemoveCompetitor(
  workspaceId: string | undefined,
  locationId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (competitorId: string) => {
      await CompetitorsApi.remove({
        path: {
          workspaceId: workspaceId as string,
          locationId: locationId as string,
          competitorId,
        },
      });
    },
    onSuccess: () => {
      invalidateCompetitorQueries(queryClient);
    },
  });
}
